import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { Card, Select, Row, Col } from 'antd';
import { useScreenWidth } from '../../../hooks/useScreen';
const { Option } = Select;

const SimulationView = ({ simulations, assetNames }) => {
  const [selectedAsset1, setSelectedAsset1] = useState(assetNames[0]);
  const [selectedAsset2, setSelectedAsset2] = useState(assetNames[1]);
  const [chartHeight, setChartHeight] = useState(400);
  const width = useScreenWidth();

  useEffect(() => {
    const newHeight = width < 1200 ? 300 : 400;
    setChartHeight(newHeight);
  }, [width]);

  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return simulations.map(sim => sim[assetIndex]);
  };

  const viridisColors = [
    '#440154', '#48186a', '#472d7b', '#424086', '#3b528b', '#33638d', '#2c728e', '#26828e',
    '#21918c', '#1fa088', '#28ae80', '#3fbc73', '#5ec962', '#84d44b', '#addc30', '#d8e219', '#fde725'
  ];

  const commonLayoutProps = {
    autosize: true,
    height: chartHeight,
    margin: { l: 50, r: 50, b: 50, t: 30, pad: 4 },
  };

  const renderScatterPlot = () => {
    const trace = {
      x: getAssetData(selectedAsset1),
      y: getAssetData(selectedAsset2),
      mode: 'markers',
      type: 'scatter',
      marker: { color: viridisColors[8], size: 5, opacity: 0.6 },
    };

    const layout = {
      ...commonLayoutProps,
      xaxis: { title: selectedAsset1 },
      yaxis: { title: selectedAsset2 },
    };

    return <Plot data={[trace]} layout={layout} useResizeHandler={true} style={{width: '100%', height: '100%'}} />;
  };

  const renderHistogram = () => {
    const traces = assetNames.map((asset, index) => ({
      x: getAssetData(asset),
      type: 'histogram',
      opacity: 0.7,
      name: asset,
      marker: { color: viridisColors[index % viridisColors.length] },
    }));

    const layout = {
      ...commonLayoutProps,
      xaxis: { title: 'Return' },
      yaxis: { title: 'Frequency' },
      barmode: 'overlay',
    };

    return <Plot data={traces} layout={layout} useResizeHandler={true} style={{width: '100%', height: '100%'}} />;
  };

  const renderBoxPlot = () => {
    const traces = assetNames.map((asset, index) => ({
      y: getAssetData(asset),
      type: 'box',
      name: asset,
      boxpoints: 'outliers',
      marker: { color: viridisColors[index % viridisColors.length] },
    }));

    const layout = {
      ...commonLayoutProps,
      yaxis: { title: 'Return' },
    };

    return <Plot data={traces} layout={layout} useResizeHandler={true} style={{width: '100%', height: '100%'}} />;
  };

  const renderSimulationPaths = () => {
    const traces = assetNames.map((asset, index) => ({
      y: getAssetData(asset),
      type: 'line',
      name: asset,
      line: { color: viridisColors[index % viridisColors.length] },
    }));

    const layout = {
      ...commonLayoutProps,
      xaxis: { title: 'Simulation Step' },
      yaxis: { title: 'Value' },
    };

    return <Plot data={traces} layout={layout} useResizeHandler={true} style={{width: '100%', height: '100%'}} />;
  };

  const renderSimulationPercentiles = () => {
    const percentiles = [5, 25, 50, 75, 95];
    const traces = assetNames.map((asset, assetIndex) => {
      const assetData = getAssetData(asset);
      return percentiles.map((percentile, index) => ({
        y: assetData.map((_, i) => {
          const sortedData = assetData.slice(0, i + 1).sort((a, b) => a - b);
          const percentileIndex = Math.floor(sortedData.length * percentile / 100);
          return sortedData[percentileIndex];
        }),
        type: 'line',
        name: `${asset} (${percentile}th percentile)`,
        line: {
          color: viridisColors[assetIndex % viridisColors.length],
          dash: index === 2 ? 'solid' : 'dash',
          width: index === 2 ? 3 : 2,
        },
      }));
    }).flat();

    const layout = {
      ...commonLayoutProps,
      xaxis: { title: 'Simulation Step' },
      yaxis: { title: 'Value' },
    };

    return <Plot data={traces} layout={layout} useResizeHandler={true} style={{width: '100%', height: '100%'}} />;
  };

  return (
    <>
      <Row gutter={[32, 32]} className={'bordered-columns'} wrap={true}>
        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Select
            style={{ width: '100%', marginBottom: 16 }}
            value={selectedAsset1}
            onChange={setSelectedAsset1}
          >
            {assetNames.map(name => (
              <Option key={name} value={name}>{name}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Select
            style={{ width: '100%', marginBottom: 16 }}
            value={selectedAsset2}
            onChange={setSelectedAsset2}
          >
            {assetNames.map(name => (
              <Option key={name} value={name}>{name}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>{renderScatterPlot()}</Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>{renderHistogram()}</Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>{renderBoxPlot()}</Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>{renderSimulationPaths()}</Col>
        <Col span={24}>{renderSimulationPercentiles()}</Col>
      </Row>
    </>
  );
};

export default SimulationView;